import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Select,
  MenuItem,
  FormControl,
  TableSortLabel,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDateSort } from '../../../../hooks/useDateSort';
import TableActionsMenu from '../../../../components/common/TableActionsMenu';
import AdpopcornPlacementDeltaTop5 from '../avatar/AdpopcornPlacementDeltaTop5';
import { AdpopcornPlacementMetrics } from '../../../../types/adpopcorn.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: AdpopcornPlacementMetrics;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

export default function AdpopcornPlacementMetricsTable({
  data,
  selectedApp,
  startDate,
  endDate
}: Props) {
  const top5PlacementStats = data.top5PlacementStats;
  const bottom5PlacementStats = data.bottom5PlacementStats;
  const placementStats = data.placementStats;
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);
  const [selectedPlacementGroup, setSelectedPlacementGroup] = useState<string>('전체');
  const [selectedPlacementUnitId, setSelectedPlacementUnitId] = useState<string>('전체');
  
  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);
  
  // placementGroup 목록 추출
  const placementGroups = useMemo(() => {
    const groups = new Set<string>();
    placementStats.forEach((stat) => {
      if (stat.placementGroup) {
        groups.add(stat.placementGroup);
      }
    });
    return ['전체', ...Array.from(groups).sort()];
  }, [placementStats]);
  
  // 선택된 placementGroup에 따른 placementUnitId 목록 추출
  const placementUnitIds = useMemo(() => {
    if (selectedPlacementGroup === '전체') {
      return ['전체'];
    }
    const unitIds = new Set<string>();
    placementStats.forEach((stat) => {
      if (stat.placementGroup === selectedPlacementGroup && stat.placementUnitId) {
        unitIds.add(stat.placementUnitId);
      }
    });
    return ['전체', ...Array.from(unitIds).sort()];
  }, [placementStats, selectedPlacementGroup]);
  
  // 필터링된 데이터
  const filteredData = useMemo(() => {
    return placementStats.filter((stat) => {
      if (selectedPlacementGroup !== '전체' && stat.placementGroup !== selectedPlacementGroup) {
        return false;
      }
      if (selectedPlacementUnitId !== '전체' && stat.placementUnitId !== selectedPlacementUnitId) {
        return false;
      }
      return true;
    });
  }, [placementStats, selectedPlacementGroup, selectedPlacementUnitId]);
  
  // 날짜별로 그룹화하여 합산
  const aggregatedData = useMemo(() => {
    const grouped = new Map<
      string,
      {
        date: string;
        revenue: number;
        req: number;
        res: number;
        imp: number;
        clk: number;
      }
    >();
    
    filteredData.forEach((stat) => {
      const existing = grouped.get(stat.date);
      if (existing) {
        existing.revenue += stat.revenue;
        existing.req += stat.req;
        existing.res += stat.res;
        existing.imp += stat.imp;
        existing.clk += stat.clk;
      } else {
        grouped.set(stat.date, {
          date: stat.date,
          revenue: stat.revenue,
          req: stat.req,
          res: stat.res,
          imp: stat.imp,
          clk: stat.clk
        });
      }
    });
    
    return Array.from(grouped.values());
  }, [filteredData]);

  // dailyStats 도 날짜별로 합산해서 사용 (osType 등 세부 구분 무시하고 날짜 단위로 집계)
  const aggregatedDailyStats = useMemo(() => {
    const grouped = new Map<
      string,
      {
        date: string;
        revenue: number;
        req: number;
        res: number;
        imp: number;
        clk: number;
      }
    >();

    data.dailyStats.forEach((stat) => {
      const existing = grouped.get(stat.date);
      if (existing) {
        existing.revenue += stat.revenue;
        existing.req += stat.req;
        existing.res += stat.res;
        existing.imp += stat.imp;
        existing.clk += stat.clk;
      } else {
        grouped.set(stat.date, {
          date: stat.date,
          revenue: stat.revenue,
          req: stat.req,
          res: stat.res,
          imp: stat.imp,
          clk: stat.clk
        });
      }
    });

    return Array.from(grouped.values());
  }, [data.dailyStats]);
  
  // placementGroup 변경 시 placementUnitId 초기화
  useEffect(() => {
    setSelectedPlacementUnitId('전체');
  }, [selectedPlacementGroup]);
  
  // 지면 선택과 광고 타입이 모두 "전체"일 때는 dailyStats 사용
  const finalData = useMemo(() => {
    if (selectedPlacementGroup === '전체' && selectedPlacementUnitId === '전체') {
      return aggregatedDailyStats;
    }
    return aggregatedData;
  }, [aggregatedDailyStats, aggregatedData, selectedPlacementGroup, selectedPlacementUnitId]);
  
  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(finalData);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const paged = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page]
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(value);
  };
  const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');

  const calculateRevenueDelta = (currentRow: typeof sortedData[0], currentIndex: number) => {
    // 정렬 방향에 따라 인덱스 조정
    const prevIndex = sortDirection === 'desc' ? currentIndex + 1 : currentIndex - 1;
    
    if (prevIndex < 0 || prevIndex >= sortedData.length) {
      return '-';
    }
    
    const prevRow = sortedData[prevIndex];
    if (!prevRow) return '-';

    const delta = currentRow.revenue - prevRow.revenue;
    const rawDeltaPercentage =
      prevRow.revenue > 0 ? Math.round((delta / prevRow.revenue) * 100) : 0;
    const deltaPercentage = rawDeltaPercentage.toLocaleString('ko-KR');

    const isPositive = delta >= 0;
    const arrow = isPositive ? '▲' : '▼';
    const sign = isPositive ? '+' : '';

    return (
      <Box
        component="span"
        sx={{
          color: isPositive ? '#D32F2F' : '#1976D2',
          display: 'inline-flex',
          alignItems: 'center'
        }}
      >
        {arrow} {deltaPercentage}% ({sign}
        {formatCurrency(delta)})
      </Box>
    );
  };

  const calculateFillRate = (req: number, imp: number) => {
    return req > 0 ? ((imp / req) * 100).toFixed(2) : '0.00';
  };

  const calculateCTR = (imp: number, clk: number) => {
    return imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
  };

  const calculateECPM = (revenue: number, imp: number) => {
    return imp > 0 ? formatCurrency((revenue / imp) * 1000) : formatCurrency(0);
  };

  const average = (vals: Array<number | undefined>) => {
    const nums = vals.filter((v): v is number => v !== undefined);
    return nums.length ? nums.reduce((s, v) => s + v, 0) / nums.length : 0;
  };

  // 합계 계산 (평균: fill rate/CTR/eCPM, 합계: 총 매출/request/response/impression/click)
  const totals = useMemo(() => {
    const totalRevenue = sortedData.reduce((sum, row) => sum + row.revenue, 0);
    const totalReq = sortedData.reduce((sum, row) => sum + row.req, 0);
    const totalRes = sortedData.reduce((sum, row) => sum + row.res, 0);
    const totalImp = sortedData.reduce((sum, row) => sum + row.imp, 0);
    const totalClk = sortedData.reduce((sum, row) => sum + row.clk, 0);

    // 행별 비율을 산술평균으로 계산 (유효 값만 포함)
    const avgFillRate = average(
      sortedData.map((row) =>
        row.req > 0 ? (row.imp / row.req) * 100 : undefined
      )
    ).toFixed(2);

    const avgCTR = average(
      sortedData.map((row) =>
        row.imp > 0 && row.clk <= row.imp ? (row.clk / row.imp) * 100 : undefined
      )
    ).toFixed(2);

    const avgECPM = formatCurrency(
      average(
        sortedData.map((row) =>
          row.imp > 0 ? (row.revenue / row.imp) * 1000 : undefined
        )
      )
    );

    return {
      totalRevenue,
      totalReq,
      totalRes,
      totalImp,
      totalClk,
      avgFillRate,
      avgCTR,
      avgECPM
    };
  }, [sortedData]);

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((row, index) => {
      // 증감률 텍스트 계산
      const prevIndex = sortDirection === 'desc' ? index + 1 : index - 1;
      let revenueDeltaText = '-';
      
      if (prevIndex >= 0 && prevIndex < sortedData.length) {
        const prevRow = sortedData[prevIndex];
        if (prevRow) {
          const delta = row.revenue - prevRow.revenue;
          const rawDeltaPercentage = prevRow.revenue > 0 ? Math.round((delta / prevRow.revenue) * 100) : 0;
          const arrow = delta >= 0 ? '▲' : '▼';
          const deltaPercentageAbs = Math.abs(rawDeltaPercentage).toLocaleString('ko-KR');
          const deltaAmountAbs = Math.abs(delta).toLocaleString('ko-KR');
          const sign = delta >= 0 ? '+' : '-';
          revenueDeltaText = `${arrow} ${deltaPercentageAbs}% (${sign}${deltaAmountAbs})`;
        }
      }

      // 퍼센트 컬럼은 소수로 전달
      const fillRate = row.req > 0 ? row.imp / row.req : 0;
      const ctr = row.imp > 0 ? row.clk / row.imp : 0;

      return [
        row.date,
        formatNumberOnly(row.revenue),
        revenueDeltaText,
        row.req.toLocaleString(),
        row.res.toLocaleString(),
        fillRate,
        row.imp.toLocaleString(),
        row.clk.toLocaleString(),
        ctr,
        formatNumberOnly((row.revenue / Math.max(row.imp, 1)) * 1000)
      ];
    });
  }, [sortedData, sortDirection]);

  return (
    <Box>
      <AdpopcornPlacementDeltaTop5
        top5PlacementStats={top5PlacementStats}
        bottom5PlacementStats={bottom5PlacementStats}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
        <TableActionsMenu 
          tableRef={tableRef} 
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="지면별지표"
        />
      </Box>

      {/* 지면 선택 영역 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 4,
          ml: 2,
          gap: 2
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          지면 선택
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            bgcolor: '#f5f5f5',
            minWidth: 300,
            maxWidth: 400
          }}
        >
          <SearchIcon sx={{ color: '#666', mr: 1 }} />
          <FormControl sx={{ flex: 1 }} variant="standard">
            <Select
              value={selectedPlacementGroup}
              onChange={(e) => setSelectedPlacementGroup(e.target.value as string)}
              displayEmpty
              renderValue={(value) => value || '전체'}
              disableUnderline
              IconComponent={KeyboardArrowDownIcon}
              inputProps={{
                'aria-label': '지면 그룹 선택'
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: 0,
                  paddingRight: '24px !important',
                  display: 'flex',
                  alignItems: 'center'
                },
                '& .MuiSelect-icon': {
                  color: '#666'
                }
              }}
            >
              {placementGroups.map((group) => (
                <MenuItem key={group} value={group}>
                  {group}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="body1" fontWeight="bold">
          광고 타입
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            bgcolor: '#f5f5f5',
            minWidth: 300,
            maxWidth: 400
          }}
        >
          <SearchIcon sx={{ color: '#666', mr: 1 }} />
          <FormControl sx={{ flex: 1 }} variant="standard">
            <Select
              value={selectedPlacementUnitId}
              onChange={(e) => setSelectedPlacementUnitId(e.target.value as string)}
              displayEmpty
              renderValue={(value) => value || '전체'}
              disableUnderline
              IconComponent={KeyboardArrowDownIcon}
              inputProps={{
                'aria-label': '광고 타입 선택'
              }}
              disabled={selectedPlacementGroup === '전체'}
              sx={{
                '& .MuiSelect-select': {
                  padding: 0,
                  paddingRight: '24px !important',
                  display: 'flex',
                  alignItems: 'center'
                },
                '& .MuiSelect-icon': {
                  color: '#666'
                }
              }}
            >
              {placementUnitIds.map((unitId) => (
                <MenuItem key={unitId} value={unitId}>
                  {unitId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* 테이블 */}
      <TableContainer
        sx={{
          overflowX: 'auto',
          mt: 2
        }}
      >
        <Table ref={tableRef}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active
                  direction={sortDirection}
                  onClick={toggleSort}
                >
                  날짜
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                총 매출
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                매출 증감률
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Request
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Response
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Fill Rate
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Impression
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Click
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                CTR
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                eCPM
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* 합계 행 */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(totals.totalRevenue)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>-</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.totalReq.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.totalRes.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgFillRate}%
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: 'pre-line', display: 'block', textAlign: 'center' }}>
                        {"조회한 기간의 평균입니다\n* (노출수 / 요청수)"}
                      </span>
                    }
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.totalImp.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.totalClk.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgCTR}%
                  <Tooltip title="조회한 기간의 평균입니다" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgECPM}
                  <Tooltip title="조회한 기간의 평균입니다" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
            {paged.map((row, pageIndex) => {
              const actualIndex = page * rowsPerPage + pageIndex;
              const revenueDelta = calculateRevenueDelta(row, actualIndex);
              const fillRate = calculateFillRate(row.req, row.imp);
              const ctr = calculateCTR(row.imp, row.clk);
              const ecpm = calculateECPM(row.revenue, row.imp);

              return (
                <TableRow key={row.date}>
                  <TableCell>{row.date}</TableCell>
                  <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                  <TableCell align="right">{revenueDelta}</TableCell>
                  <TableCell align="right">{row.req.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.res.toLocaleString()}</TableCell>
                  <TableCell align="right">{fillRate}%</TableCell>
                  <TableCell align="right">{row.imp.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.clk.toLocaleString()}</TableCell>
                  <TableCell align="right">{ctr}%</TableCell>
                  <TableCell align="right">{ecpm}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </Box>
  );
}
